"use client";

import { formatDistance } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { useDebounceCallback, useWindowSize } from "usehooks-ts";
import { MultimodalInput } from "./multimodal-input";
import { Toolbar } from "./toolbar";
import { ArtifactActions } from "./artifact-actions";
import { ArtifactCloseButton } from "./artifact-close-button";
import { ArtifactMessages } from "./artifact-messages";
import { useSidebar } from "./ui/sidebar";
import { useArtifact } from "@/hooks/use-artifact";
import { imageArtifact } from "@/artifacts/image/client";
import { codeArtifact } from "@/artifacts/code/client";
import { sheetArtifact } from "@/artifacts/sheet/client";
import { textArtifact } from "@/artifacts/text/client";
import { htmlArtifact } from "../artifacts/html/client";
import equal from "fast-deep-equal";
import { AppSidebar } from "./app-sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import BuyCreditsModal from "./BuyCreditsModal";
import { ShareLinkPopup } from "./share-link-popup";
import { useSelector } from "react-redux";

export const artifactDefinitions = [
  textArtifact,
  codeArtifact,
  imageArtifact,
  sheetArtifact,
  htmlArtifact,
];

function PureArtifact(props) {
  const {
    screenId,
    chatId,
    input,
    setInput,
    status,
    stop,
    sendMessage,
    messages,
    setMessages,
    regenerate,
    isReadonly,
    selectedVisibilityType,
    htmlContent,
    setHtmlContent,
    setIsSaveHtmlToDb,
    isFollowUp,
    setIsFollowUp,
    // userDetail,
    aiLimit,
    remainingCredits,
    // remainingWords,
    // isFreeLimit,
    setRemainingCredits,
    setCreditsUsedParent,
    setSavedHtmlUrl
  } = props;

  const { artifact, setArtifact, metadata, setMetadata } = useArtifact();
  const { shareData, userData, activeScreenId } = useSelector((state) => state.main);
  const {
    data: documents,
    isLoading: isDocumentsFetching,
    mutate: mutateDocuments,
  } = useSWR(
    artifact.documentId !== "init" && artifact.status !== "streaming"
      ? `/api/document?id=${artifact.documentId}`
      : null,
  );

  const [mode, setMode] = useState("edit");
  const [document, setDocument] = useState(null);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(-1);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const { open: isSidebarOpen, setOpenMobile } = useSidebar();
  const { mutate } = useSWRConfig();
  const [isContentDirty, setIsContentDirty] = useState(false);

  useEffect(() => {
    if (documents && documents.length > 0) {
      const mostRecentDocument = documents.at(-1);
      if (mostRecentDocument) {
        setDocument(mostRecentDocument);
        setCurrentVersionIndex(documents.length - 1);
        setArtifact((currentArtifact) => ({
          ...currentArtifact,
          content: mostRecentDocument.content ?? "",
        }));
      }
    }
  }, [documents, setArtifact]);

  useEffect(() => {
    mutateDocuments();
  }, [artifact.status, mutateDocuments]);

  const handleContentChange = useCallback(
    async (updatedContent) => {
      if (!artifact) return;

      mutate(`/api/document?id=${artifact.documentId}`, async (currentDocuments) => {
        if (!currentDocuments) return undefined;

        const currentDocument = currentDocuments.at(-1);

        if (!currentDocument || !currentDocument.content) {
          setIsContentDirty(false);
          return currentDocuments;
        }

        if (currentDocument.content !== updatedContent) {
          await fetch(`/api/document?id=${artifact.documentId}`, {
            method: "POST",
            body: JSON.stringify({
              title: artifact.title,
              content: updatedContent,
              kind: artifact.kind,
            }),
          });

          setIsContentDirty(false);

          const newDocument = {
            ...currentDocument,
            content: updatedContent,
            createdAt: new Date(),
          };

          return [...currentDocuments, newDocument];
        }
        return currentDocuments;
      }, { revalidate: false });
    },
    [artifact, mutate]
  );

  //   const handleContentChange = useCallback(
  //   async (updatedContent) => {
  //     if (!userData?._id || !screenId) return;

  //     try {
  //       const formData = new FormData();
  //       formData.append("user_id", userData._id);
  //       formData.append("screen_id", screenId);
  //       formData.append("chat_type", chatType);
  //       formData.append("is_followup", false);
  //       formData.append("creditUsed", 1);
  //       formData.append("isNewScreen", false);

  //       if (chatType === "business_plan") {
  //         formData.append("content", updatedContent);
  //       } else {
  //         formData.append("html_file_detail", updatedContent);
  //       }

  //       const res = await fetch(`${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website`, {
  //         method: "POST",
  //         body: formData,
  //       });

  //       const data = await res.json();

  //       if (data.status === 1) {
  //         console.log("Saved successfully:", data);
  //         dispatch(setSaveDbStore(data.detail));
  //         setIsContentDirty(false);
  //       } else {
  //         console.error("Failed to save:", data);
  //       }
  //     } catch (err) {
  //       console.error("Error saving content:", err);
  //     }
  //   },
  //   [userData, screenId, chatType, dispatch]
  // );
  const debouncedHandleContentChange = useDebounceCallback(handleContentChange, 2000);

  const saveContent = useCallback(
    (updatedContent, debounce) => {
      if (document && updatedContent !== document.content) {
        setIsContentDirty(true);
        if (debounce) {
          debouncedHandleContentChange(updatedContent);
        } else {
          handleContentChange(updatedContent);
        }
      }
    },
    [document, debouncedHandleContentChange, handleContentChange]
  );

  function getDocumentContentById(index) {
    if (!documents) return "";
    if (!documents[index]) return "";
    return documents[index].content ?? "";
  }

  const handleVersionChange = (type) => {
    if (!documents) return;

    if (type === "latest") {
      setCurrentVersionIndex(documents.length - 1);
      setMode("edit");
    }

    if (type === "toggle") {
      setMode((mode) => (mode === "edit" ? "diff" : "edit"));
    }

    if (type === "prev") {
      if (currentVersionIndex > 0) setCurrentVersionIndex((i) => i - 1);
    } else if (type === "next") {
      if (currentVersionIndex < documents.length - 1)
        setCurrentVersionIndex((i) => i + 1);
    }
  };

  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const isCurrentVersion =
    documents && documents.length > 0
      ? currentVersionIndex === documents.length - 1
      : true;

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const isMobile = windowWidth ? windowWidth < 768 : false;


  useEffect(() => {
    if (isMobile && artifact.isVisible) {
      setOpenMobile(false);
    }
  }, [isMobile, artifact.isVisible, setOpenMobile]);

  // Mobile device back button handler
  useEffect(() => {
    if (!isMobile) return;
    const handleCloseArtifact = (event) => {
      if (event.detail?.reason === 'deviceBackButton') {
        // Close the artifact when device back button is pressed
        setArtifact((prev) => ({ ...prev, isVisible: false }));
      }
    };

    window.addEventListener('closeArtifact', handleCloseArtifact);

    return () => {
      window.removeEventListener('closeArtifact', handleCloseArtifact);
    };
  }, [isMobile, artifact.isVisible, setArtifact]);

  useEffect(() => {
    if (artifact.isVisible) {
      window.history.pushState({}, "", window.location.href); // dummy
      window.history.pushState({ artifactOpen: true }, "", window.location.href); // artifact open
    }
  }, [artifact.isVisible]);

  // // Listen for chat deletion events to close artifact if current chat is deleted
  useEffect(() => {
    const handleChatDeleted = (event) => {
      const { screenId } = event.detail;
      // If the current screen is being deleted, close the artifact and refresh page
      if (screenId === props.screenId) {
        setArtifact((prev) => ({ ...prev, isVisible: false }));
        // Force page refresh to go back to home
        setTimeout(() => {
          window.location.href = "/";
        }, 100);
      }
    };

    window.addEventListener('chatDeleted', handleChatDeleted);

    return () => {
      window.removeEventListener('chatDeleted', handleChatDeleted);
    };
  }, [props.screenId, setArtifact]);

  const artifactDefinition = artifactDefinitions.find(
    (definition) => {
      // console.log(definition, "TEst83838", artifact.kind)
      return definition.kind === artifact.kind
    }
  );

  // console.log(artifactDefinition, "artifactDefinitionartifactDefinition")

  if (!artifactDefinition) throw new Error("Artifact definition not found!");

  useEffect(() => {
    if (artifact.documentId !== "init" && artifactDefinition.initialize) {
      artifactDefinition.initialize({
        documentId: artifact.documentId,
        setMetadata,
        setSavedHtmlUrl
      });
      // console.log(artifact.documentId, "uuuuuu-999")
    }


  }, [artifact.documentId, artifactDefinition, setMetadata, setSavedHtmlUrl]);
  return (
    <AnimatePresence>
      <motion.div
        data-testid="artifact"
        // className={`flex flex-row h-dvh w-dvw fixed top-0 left-0 z-50 bg-transparent ${isMobile ? "pl-0" : "pl-[50px]"}`}
        className={`flex flex-row h-full w-full fixed top-0 left-0 z-50 bg-transparent ${isMobile ? "pl-0" : "pl-[50px]"}`}
        initial={{ opacity: 1 }}
        animate={{
          opacity: artifact.isVisible ? 1 : 0,
          pointerEvents: artifact.isVisible ? "auto" : "none",
        }}
        exit={{ opacity: 0, transition: { delay: 0.4 } }}
      >
        {!isMobile && (
          <motion.div
            className="fixed bg-background h-dvh"
            initial={{
              width: isSidebarOpen ? windowWidth - 256 : windowWidth,
              right: 0,
            }}
            animate={{ width: windowWidth, right: 0 }}
            exit={{
              width: isSidebarOpen ? windowWidth - 256 : windowWidth,
              right: 0,
            }}
          />
        )}

        {!isMobile && (
          <div className="group flex-shrink-0 absolute left-0 top-0 h-full z-[20]">
            <SidebarProvider key={artifact?.id || "artifact"} defaultOpen={false}>
              <AppSidebar user={userData} />
            </SidebarProvider>
          </div>
        )}
       

        {!isMobile && (
          <motion.div
            className="relative lg:w-[400px] w-[300px]  dark:bg-sidebar h-dvh shrink-0"
            initial={{ opacity: 0, x: 10, scale: 1 }}
            animate={{
              opacity: 1,
              x: 0,
              scale: 1,
              transition: { delay: 0.2, type: "spring", stiffness: 200, damping: 30 },
            }}
            exit={{ opacity: 0, x: 0, scale: 1, transition: { duration: 0 } }}
          >
            <AnimatePresence>
              {!isCurrentVersion && (
                <motion.div
                  className="left-0 absolute h-dvh w-[400px] top-0 bg-zinc-900/50 z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
            </AnimatePresence>

            <div className="flex flex-col h-full justify-between">
              <ArtifactMessages
                chatId={chatId}
                status={status}
                messages={messages}
                setMessages={setMessages}
                regenerate={regenerate}
                isReadonly={isReadonly}
                artifactStatus={artifact.status}
                showCode={true}
              />

              <form className="flex flex-row gap-2 relative items-end w-full px-4 pb-4 flex-wrap pt-3 bg-white dark:bg-sidebar">
                <MultimodalInput
                  chatId={chatId}
                  input={input}
                  setInput={setInput}
                  status={status}
                  stop={stop}
                  messages={messages}
                  sendMessage={sendMessage}
                  className="bg-[transparent] dark:bg-muted"
                  setMessages={setMessages}
                  selectedVisibilityType={selectedVisibilityType}
                  htmlContent={htmlContent}
                  setHtmlContent={setHtmlContent}
                  screenId={screenId}
                  setIsSaveHtmlToDb={setIsSaveHtmlToDb}
                  isFollowUp={isFollowUp}
                  aiLimit={aiLimit}
                  remainingCredits={remainingCredits}
                  setRemainingCredits={setRemainingCredits}
                  setIsFollowUp={setIsFollowUp}
                  setCreditsUsedParent={setCreditsUsedParent}
                />
              </form>
            </div>
          </motion.div>
        )}

        <motion.div
          // className="relative flex-1 dark:bg-sidebar bg-background h-dvh flex flex-col overflow-y-scroll md:border-l dark:border-zinc-700 border-zinc-200"
          className="relative flex-1 dark:bg-sidebar bg-background min-h-screen flex flex-col overflow-y-scroll md:border-l dark:border-zinc-700 border-zinc-200"
          initial={{
            opacity: 1,
            height: artifact.boundingBox?.height || 100,
            borderRadius: 50,
          }}
          animate={{
            opacity: 1,
            x: 0,
            y: 0,
            height: windowHeight,
            width: isMobile ? windowWidth : undefined,
            borderRadius: 0,
            transition: { delay: 0, type: "spring", stiffness: 200, damping: 30, duration: 5000 },
          }}
          exit={{
            opacity: 0,
            scale: 0.5,
            transition: { delay: 0.1, type: "spring", stiffness: 600, damping: 30 },
          }}
        >
          <div className="p-2 flex flex-wrap flex-row justify-between items-center">
            <div className="flex flex-row gap-2 md:gap-4 items-center">
              <ArtifactCloseButton />
              <div className="flex flex-col">
                <div className="font-medium">{artifact.title}</div>
                {isContentDirty ? (
                  <div className="text-sm text-muted-foreground">Saving changes...</div>
                ) : document ? (
                  <div className="text-sm text-muted-foreground">
                    {`Updated ${formatDistance(new Date(document.createdAt), new Date(), {
                      addSuffix: true,
                    })}`}
                  </div>
                ) : (
                  <div />
                )}
              </div>
            </div>

            <ArtifactActions
              artifact={artifact}
              currentVersionIndex={currentVersionIndex}
              handleVersionChange={handleVersionChange}
              isCurrentVersion={isCurrentVersion}
              mode={mode}
              metadata={metadata}
              setMetadata={setMetadata}
              setShowBuyCreditsModal={setShowBuyCreditsModal}
              // isFreeLimit={isFreeLimit}
              setShareOpen={setShareOpen}
              htmlContent={htmlContent}
            // userDetail={userData}
            // remainingWords={remainingWords}
            />
          </div>

          <div className="dark:bg-muted bg-background h-full overflow-y-scroll !max-w-full items-center">
            <artifactDefinition.content
              title={artifact.title}
              // content={
              //   artifact.kind === "html"
              //     ? htmlContent : artifact.kind === "text" ? htmlContent : isCurrentVersion
              //       ? artifact.content
              //       : getDocumentContentById(currentVersionIndex)
              // }
              content={
                artifact.kind === "html"
                  ? htmlContent                     
                  : isCurrentVersion
                    ? artifact.content              
                    : getDocumentContentById(currentVersionIndex)
              }
              mode={mode}
              status={artifact.status}
              currentVersionIndex={currentVersionIndex}
              suggestions={[]}
              onSaveContent={saveContent}
              isInline={false}
              isCurrentVersion={isCurrentVersion}
              getDocumentContentById={getDocumentContentById}
              isLoading={isDocumentsFetching && !artifact.content}
              metadata={metadata}
              setMetadata={setMetadata}
              setSavedHtmlUrl={setSavedHtmlUrl}
            />

            <AnimatePresence>
              {isCurrentVersion && (
                <Toolbar
                  isToolbarVisible={isToolbarVisible}
                  setIsToolbarVisible={setIsToolbarVisible}
                  sendMessage={sendMessage}
                  status={status}
                  stop={stop}
                  setMessages={setMessages}
                  artifactKind={artifact.kind}
                />
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>

      {showBuyCreditsModal && userData && (
        <BuyCreditsModal
          show={showBuyCreditsModal}
          handleClose={() => setShowBuyCreditsModal(false)}
          userDetail={userData}
        />
      )}

      {shareOpen && shareData && userData && (
        <ShareLinkPopup
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          // isFreeLimit={isFreeLimit}
          generateData={shareData}
          htmlContent={htmlContent || undefined}
          userDetail={userData}
        />
      )}
    </AnimatePresence>
  );
}

export const Artifact = memo(PureArtifact, (prevProps, nextProps) => {
  if (prevProps.status !== nextProps.status) return false;
  if (prevProps.input !== nextProps.input) return false;
  if (!equal(prevProps.messages, nextProps.messages.length)) return false;
  if (prevProps.selectedVisibilityType !== nextProps.selectedVisibilityType)
    return false;

  return true;
});

